import { selectedPartnerProductOrderCartItemsState } from "@/atoms/item/selected-partner-product-order-cart-items-state";
import { isOpenSameCartItemModal } from "@/atoms/quote/is-open-same-cart-item-modal";
import { toastState } from "@/atoms/system/toast-state";
import { ButtonColor } from "@/common/enum/button-color";
import { PartnerOrderPath } from "@/common/enum/path";
import { itemService } from "@/services/item-service";
import { ItemDetailLineDto } from "@/types/dto/item-detail-line-dto";
import { Button, Modal } from "flowbite-react";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
import { GrayExclamationCircleIcon } from "../icon/GrayExclamationCircleIcon";
import { Body16Regular } from "../typography/Body16Regular";

export const SameCartItemModal = () => {
  const navigate = useNavigate();
  const setToast = useSetRecoilState(toastState);
  const [modal, setModal] = useRecoilState(isOpenSameCartItemModal);
  const [cartItems, setCartItems] = useRecoilState(
    selectedPartnerProductOrderCartItemsState
  );

  if (modal.cartItems.length === 0) {
    return null;
  }

  const handleSubmit = async () => {
    const filteredItems = modal.cartItems.filter((cartItem) =>
      modal.errors.every((error) => error.id !== cartItem.item.id)
    );

    const cartItem = cartItems.find((item) =>
      filteredItems.some(
        (filteredItem) => item.childItem.id === filteredItem.item.id
      )
    );

    if (cartItem) {
      setCartItems(
        cartItems.map((item) => {
          const matchingItem = filteredItems.find(
            (filteredItem) => item.childItem.id === filteredItem.item.id
          );
          return matchingItem
            ? {
                ...item,
                quantity: item.quantity + matchingItem.quantity,
              }
            : item;
        })
      );
    } else {
      const newItems = (
        await Promise.allSettled(
          filteredItems.map(async (item) => {
            try {
              const response = await itemService.getById({ id: item.item.id });
              if (response.ok) {
                return {
                  childItem: response.data,
                  quantity: item.quantity,
                };
              }
            } catch (error) {
              console.error(
                `Failed to fetch item with id ${item.item.id}:`,
                error
              );
            }
            return undefined;
          })
        )
      )

        .filter(
          (result): result is PromiseFulfilledResult<ItemDetailLineDto> =>
            result.status === "fulfilled" && result.value !== undefined
        )
        .map((result) => result.value);

      setCartItems([...cartItems, ...newItems]);
    }

    if (filteredItems.length === 0) {
      closeModal();
      alert("장바구니에 담을 수 있는 상품이 없습니다.");
      return;
    }

    navigate(PartnerOrderPath.ORDER);
    setToast({
      show: true,
      messages: ["같은 상품을 장바구니에", "담았습니다."],
    });
    closeModal();
  };

  const closeModal = () => {
    setModal({
      cartItems: [],
      errors: [],
    });
  };

  const errorMessage = modal.errors[0].message
    .split("\n")
    .map((str) => str.trim());

  return (
    <Modal size="md" show={true} onClose={closeModal}>
      <Modal.Header className="pt-[10px] px-[10px] h-[36px] border-none"></Modal.Header>
      <Modal.Body className="p-0">
        <div className="flex flex-col items-center box-border py-[20px] gap-[20px]">
          <div className="w-[42px] h-[42px] flex items-center justify-center">
            <GrayExclamationCircleIcon size={35} />
          </div>
          <div className="flex flex-col items-center">
            {errorMessage.map((message) => (
              <Body16Regular className="text-semantic-text-secondary">
                {message}
              </Body16Regular>
            ))}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="pt-0 border-none">
        <div className="flex gap-[16px] items-center justify-center w-full">
          <Button
            size="sm"
            color={ButtonColor.TRANSPARENT_OUTLINE}
            onClick={closeModal}
          >
            취소하기
          </Button>
          <Button size="sm" color={ButtonColor.PRIMARY} onClick={handleSubmit}>
            장바구니 담기
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
